import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AdminNewsletter = () => {
  const { isAuthenticated, user, token } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campaigns'); // 'campaigns' or 'subscribers'
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({ subject: '', content: '' });
  
  // Subscribers
  const [subscribers, setSubscribers] = useState([]);

  useEffect(() => {
    if (!isAuthenticated() || user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchCampaigns(),
        fetchSubscribers()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/stats`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept-Language': language || 'en' }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept-Language': language || 'en' }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/subscribers?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept-Language': language || 'en' }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscribers(data);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    setCampaignForm({ subject: '', content: '' });
    setShowCampaignModal(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({ subject: campaign.subject, content: campaign.content });
    setShowCampaignModal(true);
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.subject || !campaignForm.content) {
      toast.error(t('common.errors.fillAllFields') || 'Please fill in all fields', { autoClose: 1000 });
      return;
    }

    try {
      const url = editingCampaign
        ? `${API_URL}/api/admin/newsletter/campaigns/${editingCampaign.id}`
        : `${API_URL}/api/admin/newsletter/campaigns`;
      
      const response = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept-Language': language || 'en'
        },
        body: JSON.stringify(campaignForm)
      });

      if (response.ok) {
        toast.success(editingCampaign ? t('admin.newsletter.campaigns.update') : t('admin.newsletter.campaigns.create'), { autoClose: 1000 });
        setShowCampaignModal(false);
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.errors.failed'), { autoClose: 1000 });
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(t('common.errors.failed'), { autoClose: 1000 });
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm(t('admin.newsletter.campaigns.sendConfirm'))) {
      return;
    }

    const sendToast = toast.loading(t('common.loading'));

    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept-Language': language || 'en' }
      });

      if (response.ok) {
        const result = await response.json();
        toast.update(sendToast, {
          render: t('admin.newsletter.campaigns.sendConfirm').replace('subscribers?', `subscribers (${result.sent_count})`),
          type: 'success',
          isLoading: false,
          autoClose: 5000
        });
        fetchCampaigns();
        fetchStats();
      } else {
        const error = await response.json();
        toast.update(sendToast, {
          render: error.detail || t('common.errors.failed'),
          type: 'error',
          isLoading: false,
          autoClose: 5000
        });
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.update(sendToast, {
        render: 'Failed to send campaign',
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm(t('admin.newsletter.campaigns.deleteConfirm'))) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept-Language': language || 'en' }
      });

      if (response.ok) {
        toast.success(t('common.success.deleted'), { autoClose: 1000 });
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.errors.failed'), { autoClose: 1000 });
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const exportSubscribers = () => {
    const csv = `${t('admin.newsletter.subscribers.email')},${t('admin.newsletter.subscribers.subscribedAt')},${t('admin.newsletter.subscribers.status')}\n` + 
      subscribers.map(s => `${s.email},${new Date(s.subscribed_at).toLocaleString()},${s.is_active ? t('admin.newsletter.subscribers.active') : t('admin.newsletter.subscribers.inactive')}`).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-racing-red border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('admin.newsletter.title')}</h1>
          <p className="text-gray-600">{t('admin.newsletter.subtitle')}</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">{t('admin.newsletter.stats.totalSubscribers')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total_subscribers}</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">{t('admin.newsletter.stats.activeSubscribers')}</p>
              <p className="text-3xl font-bold text-green-600">{stats.active_subscribers}</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">{t('admin.newsletter.stats.totalCampaigns')}</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total_campaigns}</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">{t('admin.newsletter.stats.sentCampaigns')}</p>
              <p className="text-3xl font-bold text-blue-600">{stats.sent_campaigns}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`px-6 py-4 font-semibold transition ${
                  activeTab === 'campaigns'
                    ? 'text-racing-red border-b-2 border-racing-red'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('admin.newsletter.tabs.campaigns')} ({campaigns.length})
              </button>
              <button
                onClick={() => setActiveTab('subscribers')}
                className={`px-6 py-4 font-semibold transition ${
                  activeTab === 'subscribers'
                    ? 'text-racing-red border-b-2 border-racing-red'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t('admin.newsletter.tabs.subscribers')} ({subscribers.length})
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'campaigns' ? (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">{t('admin.newsletter.campaigns.title')}</h2>
                  <button
                    onClick={handleCreateCampaign}
                    className="bg-racing-red text-white px-6 py-2 rounded-lg hover:bg-red-700 transition font-semibold"
                  >
                    {t('admin.newsletter.campaigns.create')}
                  </button>
                </div>

                {campaigns.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">{t('admin.newsletter.campaigns.none')}</p>
                ) : (
                  <div className="space-y-4">
                    {campaigns.map((campaign) => (
                      <div key={campaign.id} className="border border-gray-200 rounded-lg p-4 hover:border-racing-red transition">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{campaign.subject}</h3>
                              {campaign.sent_at ? (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                                  {t('admin.newsletter.campaigns.sent')}
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                                  {t('admin.newsletter.campaigns.draft')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2"
                               dangerouslySetInnerHTML={{ __html: campaign.content }}
                            />
                            <div className="text-xs text-gray-500">
                              {t('admin.newsletter.campaigns.created')}: {new Date(campaign.created_at).toLocaleString()}
                              {campaign.sent_at && ` | ${t('admin.newsletter.campaigns.sent')}: ${new Date(campaign.sent_at).toLocaleString()} | ${t('admin.newsletter.campaigns.recipients')}: ${campaign.total_sent}`}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            {!campaign.sent_at && (
                              <>
                                <button
                                  onClick={() => handleEditCampaign(campaign)}
                                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm"
                                >
                                  {t('admin.newsletter.campaigns.edit')}
                                </button>
                                <button
                                  onClick={() => handleSendCampaign(campaign.id)}
                                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                                >
                                  {t('admin.newsletter.campaigns.send')}
                                </button>
                                <button
                                  onClick={() => handleDeleteCampaign(campaign.id)}
                                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                                >
                                  {t('admin.newsletter.campaigns.delete')}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">{t('admin.newsletter.subscribers.title')}</h2>
                  <button
                    onClick={exportSubscribers}
                    className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-900 transition font-semibold"
                  >
                    {t('admin.newsletter.subscribers.export')}
                  </button>
                </div>

                {subscribers.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">{t('admin.newsletter.subscribers.none')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold">{t('admin.newsletter.subscribers.email')}</th>
                          <th className="text-left py-3 px-4 font-semibold">{t('admin.newsletter.subscribers.subscribedAt')}</th>
                          <th className="text-left py-3 px-4 font-semibold">{t('admin.newsletter.subscribers.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.map((subscriber) => (
                          <tr key={subscriber.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">{subscriber.email}</td>
                            <td className="py-3 px-4 text-gray-600">
                              {new Date(subscriber.subscribed_at).toLocaleString()}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                subscriber.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {subscriber.is_active ? t('admin.newsletter.subscribers.active') : t('admin.newsletter.subscribers.inactive')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">
                {editingCampaign ? t('admin.newsletter.campaigns.update') : t('admin.newsletter.campaigns.create')}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">{t('admin.newsletter.campaigns.subject')} *</label>
                <input
                  type="text"
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red"
                  placeholder="e.g., New Products Launch - Spring 2026"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">{t('admin.newsletter.campaigns.content')} *</label>
                <textarea
                  value={campaignForm.content}
                  onChange={(e) => setCampaignForm({ ...campaignForm, content: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red"
                  rows="15"
                  placeholder="Enter your newsletter content here. You can use HTML tags for formatting."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt; for formatting
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3 justify-end">
              <button
                onClick={() => setShowCampaignModal(false)}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveCampaign}
                className="px-6 py-2 bg-racing-red hover:bg-red-700 text-white rounded-lg font-semibold"
              >
                {editingCampaign ? t('common.update') : t('common.create')} {t('admin.newsletter.campaigns.title')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminNewsletter;
